import { useState, useEffect, useCallback } from 'react';
import Header from '../Shared/Header';
import Sidebar from '../Shared/Sidebar';
import { API_BASE_URL } from '../../config';
import './NotificationsPage.css';

const NotificationsPage = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const userId = localStorage.getItem('userId') || 1;

    const fetchNotifications = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}get_notifications.php?user_id=${userId}`);
            const data = await response.json();
            if (data.status === 'success') {
                setNotifications(data.notifications);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const markAsRead = async (notificationId) => {
        try {
            const response = await fetch(`${API_BASE_URL}mark_notification_read.php?notification_id=${notificationId}`);
            const data = await response.json();
            if (data.status === 'success') {
                setNotifications(prev => prev.map(n => 
                    n.id === notificationId ? { ...n, is_read: 1 } : n
                ));
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllRead = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}mark_all_read.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        
        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + " years ago";
        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + " months ago";
        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + " days ago";
        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + " hours ago";
        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + " minutes ago";
        return "Just now";
    };

    const getIcon = (type) => {
        switch (type) {
            case 'AI Performance Alert': return '📊';
            case 'Referral Reward': return '🎁';
            case 'Chapter Unlock': return '🔓';
            case 'Test Reminder': return '📝';
            default: return '🔔';
        }
    };

    const unreadCount = notifications.filter(n => parseInt(n.is_read) === 0).length;

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar />

                <main className="main-content-area">
                    <Header 
                        title="🔔 Notifications" 
                        subtitle="Stay updated with your learning progress" 
                        hidePill={true} 
                        externalUnreadCount={unreadCount}
                    />
                    <div className="dashboard-scroll-container">
                        <div className="container notifications-content">
                            <div className="frosted-notifications-card border-glow">
                                <div className="notifications-header">
                                    <h2>Recent Activity</h2>
                                    {notifications.some(n => parseInt(n.is_read) === 0) && (
                                        <button className="mark-all-read" onClick={markAllRead}>Mark all as read</button>
                                    )}
                                </div>
                                <div className="notifications-list">
                                    {loading ? (
                                        <div className="loading-state">Loading notifications...</div>
                                    ) : notifications.length > 0 ? (
                                        notifications.map(notification => (
                                            <div 
                                                key={notification.id} 
                                                className={`notification-item ${parseInt(notification.is_read) === 1 ? 'read' : 'unread'}`}
                                                onClick={() => markAsRead(notification.id)}
                                            >
                                                <div className={`notification-icon ${notification.type.toLowerCase().replace(/\s+/g, '-')}`}>
                                                    {getIcon(notification.type)}
                                                </div>
                                                <div className="notification-details">
                                                    <div className="notification-top">
                                                        <h3>{notification.title}</h3>
                                                        <span className="notification-time">{getTimeAgo(notification.created_at)}</span>
                                                    </div>
                                                    <p>{notification.message}</p>
                                                </div>
                                                {parseInt(notification.is_read) === 0 && <div className="unread-dot"></div>}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-state">
                                            <p>No notifications yet.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default NotificationsPage;

